import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ViewChild,
  inject,
  input,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { catchError, EMPTY, finalize, Observable, tap } from 'rxjs';
import { Button } from 'primeng/button';
import { Card } from 'primeng/card';
import { InputText } from 'primeng/inputtext';
import { Paginator } from 'primeng/paginator';
import { Popover } from 'primeng/popover';
import { Select } from 'primeng/select';
import { Table, TableModule } from 'primeng/table';
import { Tooltip } from 'primeng/tooltip';
import { APP_PATHS } from '../../../../../../../core/config/app-paths';
import { HttpResponseService } from '../../../../../../../core/services/http-error-response.service';
import { SpinnerService } from '../../../../../../../core/services/spinner.service';
import { PreviewDataWrapperComponent } from '../../../../../../../shared/components/preview-data-wrapper/preview-data-wrapper.component';
import { TableBaseAbstractComponent } from '../../../../../../../shared/components/table-base-abstract.component';
import {
  getRolaReprezentantaOrganizacjiDropdownItems,
  RolaReprezentantaOrganizacji,
} from '../../../../../../../shared/enums/rola-reprezentanta-organizacji.enum';
import { OrganizationRepresentativeRoleLabelPipe } from '../../../../../../../shared/pipes/organization-representative-role-label.pipe';
import { AdminPanelUserDetails } from '../../../../../../../shared/models/admin-panel-user-details';
import { AdminPanelUserOrganization } from '../../../../../../../shared/models/admin-panel-user-organization';
import { TableRequestData } from '../../../../../../../shared/models/table/table-request-param';
import { TableResponse } from '../../../../../../../shared/models/table-response';
import { AdminPanelUsersNavigationContextService } from '../../../../../../../shared/services/admin-panel-users-navigation-context.service';
import { AdminPanelUsersApi } from '../../../../../../../shared/services/api/admin-panel-users.api';
import { OrganizacjaApi } from '../../../../../../../shared/services/api/organizacja.api';
import { HttpUtils } from '../../../../../../../shared/utils/http.utils';

@Component({
  selector: 'app-admin-panel-user-organizations-table',
  imports: [
    Button,
    Card,
    FormsModule,
    InputText,
    Paginator,
    Popover,
    PreviewDataWrapperComponent,
    Select,
    TableModule,
    Tooltip,
    OrganizationRepresentativeRoleLabelPipe,
  ],
  templateUrl: './admin-panel-user-organizations-table.component.html',
  styleUrl: './admin-panel-user-organizations-table.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AdminPanelUserOrganizationsTableComponent extends TableBaseAbstractComponent<AdminPanelUserOrganization> {
  private static readonly INSTITUTION_PREVIEW_LIMIT = 2;

  @ViewChild('table') override tableRef!: Table;

  readonly #adminPanelUsersApi = inject(AdminPanelUsersApi);
  readonly #organizacjaApi = inject(OrganizacjaApi);
  readonly #httpResponseService = inject(HttpResponseService);
  readonly #spinnerService = inject(SpinnerService);
  readonly #destroyRef = inject(DestroyRef);
  readonly #router = inject(Router);
  readonly #navigationContext = inject(
    AdminPanelUsersNavigationContextService,
  );

  userDetails = input.required<AdminPanelUserDetails>();
  protected institutionPopoverItems: string[] = [];

  protected tableFilters = {
    name: '',
    nip: '',
    regon: '',
    pesel: '',
    role: null as RolaReprezentantaOrganizacji | null,
    institutionType: '',
  };

  protected readonly roleOptions =
    getRolaReprezentantaOrganizacjiDropdownItems();

  constructor() {
    super();
    this.tableRequest.update((value) => ({
      ...value,
      pageSize: 5,
    }));
  }

  protected override apiCall$ = (
    params: TableRequestData,
  ): Observable<TableResponse<AdminPanelUserOrganization>> => {
    return this.#adminPanelUsersApi.getUserOrganizations$(
      this.userDetails(),
      params,
    );
  };

  protected showOrganizationDetails(organizationId: number): void {
    this.#navigationContext.setUserOrganizationDetailsContext(
      this.userDetails(),
      organizationId,
    );
    this.#router.navigate([
      `/${APP_PATHS.admin.root}/${APP_PATHS.admin.users}/${APP_PATHS.admin.userOrganizationDetailsPage}`,
    ]);
  }

  protected clearFilters(): void {
    this.tableFilters = {
      name: '',
      nip: '',
      regon: '',
      pesel: '',
      role: null,
      institutionType: '',
    };
    this.resetFilters();
  }

  protected getInstitutionPreview(value: string | null | undefined): string {
    return this.getInstitutionItems(value)
      .slice(
        0,
        AdminPanelUserOrganizationsTableComponent.INSTITUTION_PREVIEW_LIMIT,
      )
      .join(', ');
  }

  protected getInstitutionItems(value: string | null | undefined): string[] {
    return (value ?? '')
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean);
  }

  protected getInstitutionHiddenCount(
    value: string | null | undefined,
  ): number {
    return Math.max(
      this.getInstitutionItems(value).length -
        AdminPanelUserOrganizationsTableComponent.INSTITUTION_PREVIEW_LIMIT,
      0,
    );
  }

  protected getInstitutionHiddenItems(
    value: string | null | undefined,
  ): string[] {
    return this.getInstitutionItems(value).slice(
      AdminPanelUserOrganizationsTableComponent.INSTITUTION_PREVIEW_LIMIT,
    );
  }

  protected showInstitutionPopover(
    event: Event,
    popover: Popover,
    value: string | null | undefined,
  ): void {
    this.institutionPopoverItems = this.getInstitutionHiddenItems(value);
    popover.show(event);
  }

  protected downloadOrganizationDocument(
    row: AdminPanelUserOrganization,
  ): void {
    const attachments = row.attachments ?? [];

    if (!attachments.length) {
      return;
    }

    const ids = attachments.map((a) => a.id!);

    this.#spinnerService.show();

    this.#organizacjaApi
      .downloadRepresentativePowers$(ids)
      .pipe(
        tap((resp) => {
          HttpUtils.downloadFile(resp);
        }),
        catchError((err) => {
          this.#httpResponseService.showErrorMessage(
            err,
            'Nie udało się pobrać pełnomocnictw',
          );
          return EMPTY;
        }),
        finalize(() => this.#spinnerService.hide()),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe();
  }
}
